import { useState, useEffect, useCallback } from 'react';
import { IoAdd, IoTrash, IoStatsChart, IoSend, IoCloseOutline } from 'react-icons/io5';
import { notificationApi } from '../../services/api';
import { AdminModal, AdminField, ConfirmModal } from '../AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import AdminDataTable, { AdminPill } from './AdminDataTable';
import BulkActionHost from './BulkActionHost';
import EntityPicker from './EntityPicker';
import { searchUsers, searchReaders, searchFamilies, searchChurches } from './pickerSearches';
import { useBulkAction, useRowSelection, runBulk, summarizeBulk, useLatestRequest, useFirstLoad } from './useAdminTable';
import LoadingSpinner from '../LoadingSpinner';
import './NotificationManager.css';

const TYPES = ['info', 'success', 'warning', 'error', 'announcement', 'reminder', 'achievement'];
const CATEGORIES = ['system', 'reading', 'family', 'church', 'gamification', 'admin', 'general'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

// "specific" is the API's older name for a list of users; it is still listed
// in filters so existing rows can be found, but new sends use "users".
const TARGETS = {
  all: { label: 'Everyone' },
  users: { label: 'Specific users', noun: 'user', search: searchUsers },
  readers: { label: 'Specific readers', noun: 'reader', search: searchReaders },
  families: { label: 'Families', noun: 'family', search: searchFamilies },
  churches: { label: 'Churches', noun: 'church', search: searchChurches }
};

const TYPE_ICONS = {
  info: 'ℹ️',
  success: '✅',
  warning: '⚠️',
  error: '❌',
  announcement: '📢',
  reminder: '⏰',
  achievement: '🏆'
};

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

// Shown with the zone name, so an admin knows whose clock a time is on
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZoneName: 'short'
  });
};

// datetime-local has no zone; it is the admin's local wall clock. Send an ISO
// instant so the server does not read it as UTC.
const toIsoInstant = (localValue) => (localValue ? new Date(localValue).toISOString() : undefined);

const isScheduled = (notification) => notification.scheduledAt && new Date(notification.scheduledAt) > new Date();
const isExpired = (notification) => notification.expiresAt && new Date(notification.expiresAt) < new Date();

const blankForm = () => ({
  title: '',
  message: '',
  type: 'info',
  priority: 'normal',
  category: 'general',
  targetType: 'all',
  recipients: [], // [{ id, label }]
  scheduledAt: '',
  expiresAt: ''
});

/**
 * Picks the people a targeted notification goes to. Before this the form
 * offered targeted types with no way to choose anyone, so every targeted send
 * went out with an empty list.
 */
const RecipientPicker = ({ target, recipients, onChange }) => {
  const config = TARGETS[target];
  const add = (id, label) => {
    if (!id || recipients.some((item) => item.id === id)) return;
    onChange([...recipients, { id, label }]);
  };
  const remove = (id) => onChange(recipients.filter((item) => item.id !== id));

  return (
    <div className="recipient-picker">
      {recipients.length > 0 && (
        <ul className="recipient-chips">
          {recipients.map((item) => (
            <li key={item.id}>
              <span>{item.label}</span>
              <button type="button" aria-label={`Remove ${item.label}`} onClick={() => remove(item.id)}>
                <IoCloseOutline aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <EntityPicker
        ariaLabel={`add ${config.noun}`}
        placeholder={`Add a ${config.noun}…`}
        value=""
        search={config.search}
        onChange={add}
      />
    </div>
  );
};

const CreateNotificationModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const targeted = form.targetType !== 'all';

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (targeted && form.recipients.length === 0) {
      setError(`Pick at least one ${TARGETS[form.targetType].noun}, or send to everyone.`);
      return;
    }
    if (form.scheduledAt && form.expiresAt && form.expiresAt <= form.scheduledAt) {
      setError('The expiry must be after the scheduled time.');
      return;
    }

    const payload = {
      title: form.title,
      message: form.message,
      type: form.type,
      priority: form.priority,
      category: form.category,
      targetType: form.targetType,
      ...(targeted ? { targetIds: form.recipients.map((item) => item.id) } : {}),
      ...(form.scheduledAt ? { scheduledAt: toIsoInstant(form.scheduledAt) } : {}),
      ...(form.expiresAt ? { expiresAt: toIsoInstant(form.expiresAt) } : {})
    };

    try {
      setSaving(true);
      setError(null);
      await notificationApi.createNotification(payload);
      onCreated(`Created "${form.title}"${form.scheduledAt ? ', scheduled' : ''}.`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create notification.'));
      setSaving(false);
    }
  };

  return (
    <AdminModal
      wide
      title="New notification"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="notification-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create notification'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="notification-form" onSubmit={handleSubmit}>
        <AdminField label="Title">
          <input type="text" value={form.title} onChange={set('title')} required maxLength={200} />
        </AdminField>
        <AdminField label="Message">
          <textarea value={form.message} onChange={set('message')} required rows={4} />
        </AdminField>

        <div className="notification-form-row">
          <AdminField label="Type">
            <select value={form.type} onChange={set('type')}>
              {TYPES.map((type) => <option key={type} value={type}>{capitalize(type)}</option>)}
            </select>
          </AdminField>
          <AdminField label="Priority">
            <select value={form.priority} onChange={set('priority')}>
              {PRIORITIES.map((priority) => <option key={priority} value={priority}>{capitalize(priority)}</option>)}
            </select>
          </AdminField>
          <AdminField label="Category">
            <select value={form.category} onChange={set('category')}>
              {CATEGORIES.map((category) => <option key={category} value={category}>{capitalize(category)}</option>)}
            </select>
          </AdminField>
        </div>

        <AdminField label="Send to">
          <select
            value={form.targetType}
            onChange={(event) => setForm((prev) => ({ ...prev, targetType: event.target.value, recipients: [] }))}
          >
            {Object.entries(TARGETS).map(([key, target]) => <option key={key} value={key}>{target.label}</option>)}
          </select>
        </AdminField>

        {targeted && (
          <AdminField
            label={`${capitalize(TARGETS[form.targetType].noun)} recipients`}
            hint={form.targetType === 'families' || form.targetType === 'churches'
              ? 'Every member account receives it.'
              : undefined}
          >
            <RecipientPicker
              target={form.targetType}
              recipients={form.recipients}
              onChange={(recipients) => setForm((prev) => ({ ...prev, recipients }))}
            />
          </AdminField>
        )}

        <div className="notification-form-row">
          <AdminField label="Schedule (optional)" hint="Your local time. Leave empty to create it now.">
            <input type="datetime-local" value={form.scheduledAt} onChange={set('scheduledAt')} />
          </AdminField>
          <AdminField label="Expires (optional)" hint="Your local time.">
            <input type="datetime-local" value={form.expiresAt} onChange={set('expiresAt')} />
          </AdminField>
        </div>
      </form>
    </AdminModal>
  );
};

const StatsModal = ({ notification, onClose }) => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    notificationApi.getNotificationStats(notification.id)
      .then((response) => setStats(response.data.stats))
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load notification statistics.')));
  }, [notification.id]);

  return (
    <AdminModal
      title={`Statistics: ${notification.title}`}
      onClose={onClose}
      footer={<button type="button" className="admin-modal-btn" onClick={onClose}>Close</button>}
    >
      {error && <div className="admin-modal-error">{error}</div>}
      {!stats && !error && <LoadingSpinner size="small" message="Loading statistics…" />}
      {stats && (
        <>
          <div className="notification-stats-grid">
            <div><strong>{stats.total}</strong><span>Recipients</span></div>
            <div><strong>{stats.read}</strong><span>Read ({stats.readRate}%)</span></div>
            <div><strong>{stats.delivered}</strong><span>Delivered ({stats.deliveryRate}%)</span></div>
          </div>
          {stats.breakdown?.length > 0 && (
            <table className="notification-breakdown">
              <thead>
                <tr><th>Delivery</th><th>Read</th><th>Action</th><th>Count</th></tr>
              </thead>
              <tbody>
                {stats.breakdown.map((item, index) => (
                  <tr key={index}>
                    <td>{item.deliveryStatus}</td>
                    <td>{item.isRead ? 'Yes' : 'No'}</td>
                    <td>{item.userAction}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </AdminModal>
  );
};

const NotificationManager = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [filters, setFilters] = useState({ type: '', category: '', targetType: '', isActive: '' });
  const [openNotification, setOpenNotification] = useState(null);

  const [creating, setCreating] = useState(false);
  const [viewingStats, setViewingStats] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sending, setSending] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const beginRequest = useLatestRequest();
  const firstLoad = useFirstLoad(loading);

  const loadNotifications = useCallback(async () => {
    const request = beginRequest();
    try {
      setLoading(true);
      const params = Object.fromEntries(
        Object.entries({ page: currentPage, limit: 20, ...filters }).filter(([, value]) => value !== '')
      );
      const response = await notificationApi.getAllNotifications(params);
      if (!request.isCurrent()) return;
      setNotifications(response.data.notifications || []);
      setPagination({
        total: response.data.pagination?.total ?? 0,
        pages: response.data.pagination?.pages || 1
      });
      setError(null);
    } catch (err) {
      if (!request.isCurrent()) return;
      setError(apiErrorMessage(err, 'Failed to load notifications.'));
    } finally {
      if (request.isCurrent()) setLoading(false);
    }
  }, [beginRequest, currentPage, filters]);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const setFilter = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
    setCurrentPage(1);
  };

  const closeDialogs = () => {
    setDeleting(null);
    setSending(null);
    setDialogError(null);
  };

  const runDialog = async (action, failure, success) => {
    try {
      setDialogBusy(true);
      setDialogError(null);
      await action();
      closeDialogs();
      setNotice(success);
      loadNotifications();
    } catch (err) {
      setDialogError(apiErrorMessage(err, failure));
    } finally {
      setDialogBusy(false);
    }
  };

  const selection = useRowSelection(`${currentPage}|${filters.type}|${filters.category}|${filters.targetType}|${filters.isActive}`);
  const bulk = useBulkAction((result, text) => {
    selection.clear();
    setNotice(text);
    loadNotifications();
  });

  if (firstLoad) return <LoadingSpinner size="large" message="Loading notifications..." />;

  return (
    <div className="notification-manager">
      <div className="notification-toolbar">
        <div className="notification-filters">
          <select value={filters.type} onChange={setFilter('type')} aria-label="Type">
            <option value="">All types</option>
            {TYPES.map((type) => <option key={type} value={type}>{capitalize(type)}</option>)}
          </select>
          <select value={filters.category} onChange={setFilter('category')} aria-label="Category">
            <option value="">All categories</option>
            {CATEGORIES.map((category) => <option key={category} value={category}>{capitalize(category)}</option>)}
          </select>
          <select value={filters.targetType} onChange={setFilter('targetType')} aria-label="Audience">
            <option value="">Any audience</option>
            {Object.entries(TARGETS).map(([key, target]) => <option key={key} value={key}>{target.label}</option>)}
            <option value="specific">Specific users (older sends)</option>
          </select>
          <select value={filters.isActive} onChange={setFilter('isActive')} aria-label="Status">
            <option value="">Any status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
        <button type="button" className="create-notification-btn" onClick={() => setCreating(true)}>
          <IoAdd aria-hidden="true" /> New notification
        </button>
      </div>

      {notice && (
        <div className="notification-notice" role="status">
          <span>{notice}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}>&times;</button>
        </div>
      )}

      <AdminDataTable
        title={`Notifications (${pagination.total})`}
        rows={notifications}
        getRowLabel={(notification) => notification.title}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={loadNotifications}
        emptyTitle="No notifications found"
        emptyMessage="Nothing matches these filters."
        expandedId={openNotification}
        onToggleExpand={(id) => setOpenNotification(openNotification === id ? null : id)}
        renderExpanded={(notification) => (
          <div className="notification-details">
            <p>{notification.message}</p>
            <div className="detail-row"><span>Created</span><span>{formatDate(notification.createdAt)}</span></div>
            {notification.scheduledAt && <div className="detail-row"><span>Scheduled</span><span>{formatDate(notification.scheduledAt)}</span></div>}
            {notification.expiresAt && <div className="detail-row"><span>Expires</span><span>{formatDate(notification.expiresAt)}</span></div>}
            {notification.sentAt && <div className="detail-row"><span>Sent</span><span>{formatDate(notification.sentAt)}</span></div>}
            {notification.targetIds?.length > 0 && (
              <div className="detail-row"><span>Recipients</span><span>{notification.targetIds.length} selected</span></div>
            )}
          </div>
        )}
        pagination={{
          page: currentPage,
          totalPages: pagination.pages,
          hasPrev: currentPage > 1,
          hasNext: currentPage < pagination.pages,
          onPageChange: setCurrentPage
        }}
        bulkActions={[
          {
            label: 'Delete selected',
            danger: true,
            onClick: (rows) => bulk.ask({
              title: `Delete ${rows.length} notification${rows.length === 1 ? '' : 's'}`,
              confirmLabel: 'Delete',
              body: <p className="admin-modal-hint">These notifications are removed. People who already received them are not notified again.</p>,
              run: () => runBulk(rows, (row) => notificationApi.deleteNotification(row.id)),
              summarize: (result) => summarizeBulk('Deleted', 'notification', result)
            })
          }
        ]}
        columns={[
          {
            key: 'title',
            header: 'Notification',
            render: (notification) => (
              <span className="admin-cell-strong">
                <span aria-hidden="true">{TYPE_ICONS[notification.type] || '📢'}</span> {notification.title}
              </span>
            )
          },
          { key: 'priority', header: 'Priority', render: (notification) => <AdminPill>{notification.priority}</AdminPill> },
          { key: 'category', header: 'Category', render: (notification) => notification.category },
          {
            key: 'target',
            header: 'Audience',
            render: (notification) => {
              const label = TARGETS[notification.targetType]?.label || 'Specific users';
              const count = notification.targetIds?.length;
              return count ? `${label} (${count})` : label;
            }
          },
          {
            key: 'status',
            header: 'Status',
            render: (notification) => (
              <>
                {isScheduled(notification) && <AdminPill tone="info">Scheduled</AdminPill>}
                {isExpired(notification) && <AdminPill tone="warn">Expired</AdminPill>}
                {notification.sentAt && <AdminPill tone="ok">Sent</AdminPill>}
              </>
            )
          }
        ]}
        renderActions={(notification) => (
          <>
            <button type="button" onClick={() => setViewingStats(notification)} title="View statistics">
              <IoStatsChart aria-hidden="true" /> Stats
            </button>
            {isScheduled(notification) && (
              <button type="button" onClick={() => setSending(notification)} title="Send now instead of at the scheduled time">
                <IoSend aria-hidden="true" /> Send now
              </button>
            )}
            <button type="button" className="is-danger" onClick={() => setDeleting(notification)} title="Delete">
              <IoTrash aria-hidden="true" /> Delete
            </button>
          </>
        )}
      />
      <BulkActionHost bulk={bulk} />

      {creating && (
        <CreateNotificationModal
          onClose={() => setCreating(false)}
          onCreated={(text) => {
            setCreating(false);
            setNotice(text);
            loadNotifications();
          }}
        />
      )}

      {viewingStats && <StatsModal notification={viewingStats} onClose={() => setViewingStats(null)} />}

      {sending && (
        <ConfirmModal
          title="Send this notification now"
          confirmLabel="Send now"
          danger={false}
          busy={dialogBusy}
          error={dialogError}
          onCancel={closeDialogs}
          onConfirm={() => runDialog(
            () => notificationApi.sendScheduledNotification(sending.id),
            'Failed to send this notification.',
            `Sent "${sending.title}".`
          )}
        >
          <p className="admin-modal-hint">
            &ldquo;{sending.title}&rdquo; was scheduled for {formatDate(sending.scheduledAt)}. Sending now
            delivers it to its audience immediately. This cannot be undone.
          </p>
        </ConfirmModal>
      )}

      {deleting && (
        <ConfirmModal
          title="Delete this notification"
          confirmLabel="Delete notification"
          busy={dialogBusy}
          error={dialogError}
          onCancel={closeDialogs}
          onConfirm={() => runDialog(
            () => notificationApi.deleteNotification(deleting.id),
            'Failed to delete this notification.',
            `Deleted "${deleting.title}".`
          )}
        >
          <p className="admin-modal-hint">
            &ldquo;{deleting.title}&rdquo; is removed for everyone. Notifications already delivered
            to a device are not recalled.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default NotificationManager;
